import * as Dialog from "@radix-ui/react-dialog";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import type { HTMLAttributes, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import buttonDropdownStyles from "../ButtonDropdown/ButtonDropdown.module.scss";
import { AppHeaderMenuTrigger } from "../AppHeader/AppHeader";
import { Input } from "../Input";
import { cx } from "../../utils/cx";
import styles from "./StoreSelector.module.scss";
import Icon from "../../../base/components/Icon";

const SearchIcon = () => <Icon icon="search" />;

export type StoreSelectorOption = {
    id: string;
    label: string;
    disabled?: boolean;
};

export type StoreSelectorProps = Omit<
    HTMLAttributes<HTMLDivElement>,
    "onChange"
> & {
    /** Store rows (filtered client-side by the search query). */
    options: StoreSelectorOption[];
    /** Selected store id. */
    value: string;
    /** Called when the user picks a row (menu closes after select). */
    onValueChange?: (id: string) => void;
    /** Small label above the value on the trigger. Default: “Current store”. */
    metaLabel?: ReactNode;
    /** Floating label on the search field (e.g. “Search products”). Default: “Search”. */
    searchLabel?: string;
    /**
     * Shown when the user has typed a search and no option matches.
     * Default: “No results found”.
     */
    emptySearchMessage?: ReactNode;
    /** Sheet title on viewports &lt;768px. Default: “Select store”. */
    sheetTitle?: ReactNode;
    /** Leading icon on the trigger; defaults to storefront in a raised circular tile. */
    icon?: ReactNode;
    /**
     * Loading: single-line “Loading stores...” (no store id), menu stays closed.
     */
    loading?: boolean;
    /** `aria-label` on the trigger (default: “Select store”). */
    "aria-label"?: string;
};

const MOBILE_MEDIA = "(max-width: 767px)";

const CloseGlyph = () => (
    <svg aria-hidden fill="none" height="20" viewBox="0 0 20 20" width="20">
        <path
            d="M6 6l8 8M14 6l-8 8"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="1.8"
        />
    </svg>
);

/**
 * Store selector — pill trigger (`AppHeaderMenuTrigger` + `surface="pill"`).
 * ≥768px: dropdown flyout. &lt;768px: bottom sheet + scrim (Settings-sections pattern).
 */
export function StoreSelector({
    options,
    value,
    onValueChange,
    metaLabel = "Current store",
    searchLabel = "Search",
    emptySearchMessage = "No results found",
    sheetTitle = "Select store",
    icon,
    loading = false,
    "aria-label": ariaLabel = "Select store",
    className,
    ...rest
}: StoreSelectorProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== "undefined"
            ? window.matchMedia(MOBILE_MEDIA).matches
            : false,
    );

    useEffect(() => {
        const media = window.matchMedia(MOBILE_MEDIA);
        const onChange = () => {
            const mobile = media.matches;
            setIsMobile(mobile);
            if (!mobile) {
                setOpen(false);
                setQuery("");
            }
        };
        media.addEventListener("change", onChange);
        return () => media.removeEventListener("change", onChange);
    }, []);

    const selected = useMemo(
        () => options.find(o => o.id === value),
        [options, value],
    );

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) {
            return options;
        }
        return options.filter(o => o.label.toLowerCase().includes(q));
    }, [options, query]);

    const trimmedQuery = query.trim();
    const showNoSearchResults =
        trimmedQuery.length > 0 && filtered.length === 0;

    const triggerIcon = icon ?? (
        <span className={styles.triggerIconWell}>
            <Icon icon="store" />
        </span>
    );

    const handleOpenChange = (next: boolean) => {
        if (loading && next) {
            return;
        }
        setOpen(next);
        if (!next) {
            setQuery("");
        }
    };

    const selectOption = (id: string) => {
        if (loading) {
            return;
        }
        onValueChange?.(id);
        handleOpenChange(false);
    };

    const triggerLabel = loading
        ? "Loading stores..."
        : selected?.label ?? value;
    const triggerMeta = loading ? undefined : metaLabel;

    const searchField = (
        <div className={styles.searchSlot}>
            <Input
                autoComplete="off"
                density="compact"
                fullWidth
                iconLeft={<SearchIcon />}
                iconRight={
                    query ? (
                        <button
                            aria-label="Clear search"
                            className={styles.clearSearch}
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                setQuery("");
                            }}
                            onPointerDown={e => e.preventDefault()}
                            type="button"
                        >
                            <Icon icon="close" />
                        </button>
                    ) : undefined
                }
                label={searchLabel}
                labelBehavior="inline"
                onChange={e => setQuery(e.target.value)}
                /* Radix DropdownMenu typeahead steals focus on keypress — desktop only. */
                onKeyDown={e => e.stopPropagation()}
                onKeyUp={e => e.stopPropagation()}
                role="searchbox"
                type="text"
                value={query}
            />
        </div>
    );

    const optionsList = showNoSearchResults ? (
        <div className={styles.emptyState} role="status">
            {emptySearchMessage}
        </div>
    ) : (
        filtered.map(opt => (
            <button
                className={styles.menuItem}
                data-selected={opt.id === value ? true : undefined}
                disabled={opt.disabled}
                key={opt.id}
                onClick={() => selectOption(opt.id)}
                type="button"
            >
                {opt.label}
            </button>
        ))
    );

    const trigger = (
        <AppHeaderMenuTrigger
            aria-busy={loading || undefined}
            aria-expanded={open}
            aria-haspopup={isMobile ? "dialog" : "menu"}
            aria-label={ariaLabel}
            className={styles.triggerInHeader}
            compactOnMobile
            data-loading={loading ? "" : undefined}
            data-state={open ? "open" : "closed"}
            data-store-selector="true"
            icon={triggerIcon}
            label={triggerLabel}
            metaLabel={triggerMeta}
            surface="pill"
        />
    );

    return (
        <div className={cx(styles.root, className)} {...rest}>
            {isMobile ? (
                <>
                    <AppHeaderMenuTrigger
                        aria-busy={loading || undefined}
                        aria-expanded={open}
                        aria-haspopup="dialog"
                        aria-label={ariaLabel}
                        className={styles.triggerInHeader}
                        compactOnMobile
                        data-loading={loading ? "" : undefined}
                        data-state={open ? "open" : "closed"}
                        data-store-selector="true"
                        icon={triggerIcon}
                        label={triggerLabel}
                        metaLabel={triggerMeta}
                        onClick={() => {
                            if (!loading) {
                                handleOpenChange(true);
                            }
                        }}
                        surface="pill"
                        type="button"
                    />
                    <Dialog.Root
                        onOpenChange={handleOpenChange}
                        open={loading ? false : open}
                    >
                        <Dialog.Portal>
                            <Dialog.Overlay className={styles.sheetOverlay} />
                            <Dialog.Content
                                aria-describedby={undefined}
                                className={styles.sheet}
                                onCloseAutoFocus={e => e.preventDefault()}
                            >
                                <div className={styles.sheetHeader}>
                                    <Dialog.Title className={styles.sheetTitle}>
                                        {sheetTitle}
                                    </Dialog.Title>
                                    <Dialog.Close asChild>
                                        <button
                                            aria-label="Close store selector"
                                            className={styles.sheetClose}
                                            type="button"
                                        >
                                            <CloseGlyph />
                                        </button>
                                    </Dialog.Close>
                                </div>
                                <div className={styles.sheetBody}>
                                    {searchField}
                                    <div className={styles.sheetList}>
                                        {optionsList}
                                    </div>
                                </div>
                            </Dialog.Content>
                        </Dialog.Portal>
                    </Dialog.Root>
                </>
            ) : (
                <DropdownMenu.Root
                    onOpenChange={handleOpenChange}
                    open={loading ? false : open}
                >
                    <DropdownMenu.Trigger asChild>
                        {trigger}
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                        <DropdownMenu.Content
                            align="end"
                            className={cx(
                                buttonDropdownStyles.content,
                                styles.menuContent,
                                styles.menuContentLayout,
                            )}
                            loop
                            onCloseAutoFocus={e => e.preventDefault()}
                            side="bottom"
                            sideOffset={6}
                        >
                            {searchField}
                            <div className={styles.menuList}>
                                {showNoSearchResults ? (
                                    <div
                                        className={styles.emptyState}
                                        role="status"
                                    >
                                        {emptySearchMessage}
                                    </div>
                                ) : (
                                    filtered.map(opt => (
                                        <DropdownMenu.Item
                                            className={styles.menuItem}
                                            data-selected={
                                                opt.id === value
                                                    ? true
                                                    : undefined
                                            }
                                            disabled={opt.disabled}
                                            key={opt.id}
                                            onSelect={() => {
                                                onValueChange?.(opt.id);
                                                handleOpenChange(false);
                                            }}
                                        >
                                            {opt.label}
                                        </DropdownMenu.Item>
                                    ))
                                )}
                            </div>
                        </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                </DropdownMenu.Root>
            )}
        </div>
    );
}

StoreSelector.displayName = "StoreSelector";
